import React from 'react';
import Box from 'relements/build/esm/ui/box';
import Text from 'relements/build/esm/ui/text';
import Toggle from 'relements/build/esm/input/toggle';
import AddIconV2 from 'relements/build/esm/ui/icons/AddIconV2';
import BinIcon from 'relements/build/esm/ui/icons/BinIcon';
import InfoIconV4 from 'relements/build/esm/ui/icons/InfoIconV4';
import {
  CatalogStoreKeyDropdown,
  CustomPromptsSearchSettingsWrapper,
  ExactMatchCheckbox,
  JsonFunctionInput,
  SearchFunctionKeyInput,
  DefaultCursorWrapper,
  StyledIcon,
  SystemPromptInput,
} from '../StyledComponents';
import { ACTIONS } from '../const';

const getUpdatedGptToElasticSearchKeys = (data, payload) => {
  return data.map((searchKey) => {
    if (searchKey.id === payload.id) {
      if (payload.key === 'search_key') {
        return {
          ...searchKey,
          key: payload[payload.key],
        };
      }
      if (payload.key === 'catalog_store_key') {
        return {
          ...searchKey,
          value: {
            ...searchKey.value,
            keys: payload[payload.key],
          },
        };
      }
      return {
        ...searchKey,
        value: {
          ...searchKey.value,
          exact_match: payload[payload.key],
        },
      };
    }
    return searchKey;
  });
};

const CustomPromptsSearchSettings = ({ state, disableAllFields, dispatch }) => {
  const {
    isFaqAllowed,
    isBuyingGuidanceAllowed,
    isSearchAllowed,
    isCustomizePromptsSearchSettingsAccordionActive,
    systemPrompt,
    gptToElasticSearchKeys,
    defaultElasticSearchKeys,
    defaultGptFunctions,
    errors,
    jsonFunctions,
    jsonFunctionError,
  } = state;
  const gptFunctionsKeyStateMapping = {
    search: isSearchAllowed,
    buying_guidance: isBuyingGuidanceAllowed,
    product_inquiry: isFaqAllowed,
  };

  const dispatchJsonError = (key, error) => {
    dispatch({
      type: ACTIONS.SET_JSON_FUNCTIONS_ERROR,
      payload: { ...jsonFunctionError, [key]: error },
    });
  };

  const onGptFunctionsChange = (key, value) => {
    let type = ACTIONS.SET_SEARCH_FUNCTION;
    if (key === 'buying_guidance') {
      type = ACTIONS.SET_BUYING_GUIDANCE;
    }
    if (key === 'product_inquiry') {
      type = ACTIONS.SET_PRODUCT_QA;
    }
    dispatch({ type, payload: value });

    if (value && !jsonFunctions[key]?.trim()) {
      dispatchJsonError(key, 'Invalid JSON');
    }

    if (!value && jsonFunctionError[key]) {
      dispatchJsonError(key, null);
    }
  };

  const handleBlurForGptFunctions = (key, value) => {
    try {
      if (value && value.trim() !== '') {
        JSON.parse(value);
        dispatchJsonError(key, null);
      } else if (!gptFunctionsKeyStateMapping[key]) {
        dispatchJsonError(key, null);
      } else if (gptFunctionsKeyStateMapping[key] && !value) {
        dispatchJsonError(key, 'Invalid JSON');
      }
    } catch (err) {
      dispatchJsonError(key, 'Invalid JSON');
    }
  };

  const onKeyChange = (payload) => {
    let newGptToElasticSearchKeys = gptToElasticSearchKeys;
    if (payload.key === 'remove') {
      newGptToElasticSearchKeys = gptToElasticSearchKeys.filter(
        (keys) => keys.id !== payload.value.id
      );
    } else if (payload.key === 'new_key') {
      newGptToElasticSearchKeys = [...gptToElasticSearchKeys, payload.value];
    } else {
      newGptToElasticSearchKeys = getUpdatedGptToElasticSearchKeys(
        gptToElasticSearchKeys,
        payload
      );
    }
    dispatch({
      type: ACTIONS.SET_GPT_TO_ELASTIC_SEARCH_KEYS,
      payload: newGptToElasticSearchKeys,
    });
  };

  return (
    <CustomPromptsSearchSettingsWrapper
      padding="16px"
      flexDirection="column"
      onClick={() => {
        if (!isCustomizePromptsSearchSettingsAccordionActive) {
          dispatch({
            type: ACTIONS.SET_CUSTOM_PROMPTS_ACCORDION_ACTIVE,
            payload: true,
          });
        }
      }}
    >
      <Box
        padding="0px"
        justifyContent="space-between"
        onClick={() => {
          if (isCustomizePromptsSearchSettingsAccordionActive) {
            dispatch({
              type: ACTIONS.SET_CUSTOM_PROMPTS_ACCORDION_ACTIVE,
              payload: false,
            });
          }
        }}
      >
        <Text color="black.haptik" variant="body.md">
          Customize Prompts & Search Settings
        </Text>
        <StyledIcon
          src="angle-down"
          active={isCustomizePromptsSearchSettingsAccordionActive}
        />
      </Box>

      {isCustomizePromptsSearchSettingsAccordionActive && (
        <DefaultCursorWrapper>
          <Text variant="body.md" style={{ margin: '24px 0 12px' }}>
            System Prompt
          </Text>
          <SystemPromptInput
            themed
            multiline
            placeholder="Enter System Prompt here"
            onChange={(value) => {
              dispatch({
                type: ACTIONS.SET_SYSTEM_PROMPT,
                payload: value,
              });
            }}
            value={systemPrompt}
            error={errors.systemPrompt}
            isMultilineError={errors.systemPrompt}
            disabled={disableAllFields}
          />
          <Box padding={0} style={{ gap: '15px' }}>
            {Object.keys(defaultGptFunctions).map((functionKey) => (
              <Box padding={0} flexDirection="column">
                <Box
                  padding={0}
                  alignItems="center"
                  marginBottom="6px"
                  marginTop="24px"
                  style={{ gap: '8px' }}
                >
                  <Text color="black.haptik" variant="body.md">
                    {defaultGptFunctions[functionKey]}
                  </Text>
                  <Box padding={0} width="36px">
                    <Toggle
                      themed
                      value={gptFunctionsKeyStateMapping[functionKey]}
                      onChange={(value) =>
                        onGptFunctionsChange(functionKey, value)
                      }
                      disabled={disableAllFields}
                    />
                  </Box>
                </Box>
                <Box padding={0}>
                  <JsonFunctionInput
                    themed
                    multiline
                    placeholder={`Enter ${defaultGptFunctions[functionKey]} here`}
                    onChange={(value) => {
                      dispatch({
                        type: ACTIONS.SET_JSON_FUNCTIONS,
                        payload: {
                          ...jsonFunctions,
                          [functionKey]: value.trim(),
                        },
                      });
                    }}
                    value={jsonFunctions[functionKey]}
                    onBlur={() =>
                      handleBlurForGptFunctions(
                        functionKey,
                        jsonFunctions[functionKey]
                      )
                    }
                    error={jsonFunctionError[functionKey]}
                    isMultilineError={jsonFunctionError[functionKey]}
                    disabled={
                      disableAllFields ||
                      !gptFunctionsKeyStateMapping[functionKey]
                    }
                  />
                </Box>
              </Box>
            ))}
          </Box>
          <Text
            variant="body.md"
            color="black.haptik"
            style={{ margin: '24px 0 16px' }}
          >
            Customize Catalog Search
          </Text>
          <Box padding={0} flexDirection="column">
            {gptToElasticSearchKeys.length ? (
              <>
                <Box padding={0}>
                  <Box padding={0} alignItems="center">
                    <Text color="black.haptik" variant="body.sm">
                      Search Function Keys
                    </Text>
                    <InfoIconV4
                      size="small"
                      iconColor="grey.dark"
                      marginLeft="8px"
                      marginRight="16px"
                      tooltip="These are the keys used to search within the catalog. You can specify multiple keys from the search function to refine the search results."
                      tooltipPosition="TOP"
                    />
                  </Box>
                  <Box padding={0} alignItems="center" marginLeft="249px">
                    <Text color="black.haptik" variant="body.sm">
                      Catalog Store Key
                    </Text>
                  </Box>
                </Box>
                <Box padding={0} flexDirection="column">
                  {gptToElasticSearchKeys.map((searchKey) => (
                    <Box
                      padding={0}
                      alignItems="center"
                      style={{ gap: '16px' }}
                      marginBottom="10px"
                      key={searchKey.id}
                    >
                      <SearchFunctionKeyInput
                        themed
                        placeholder="Enter your search key"
                        onChange={(value) => {
                          onKeyChange({
                            key: 'search_key',
                            id: searchKey.id,
                            search_key: value,
                          });
                        }}
                        value={searchKey.key}
                        disabled={disableAllFields}
                      />
                      <CatalogStoreKeyDropdown
                        themed
                        style={{ width: '390px' }}
                        withSearch
                        withMultiple
                        withCheckbox
                        placeholder="Select catalog store key"
                        options={Object.keys(defaultElasticSearchKeys).map(
                          (key) => ({
                            key,
                            value: defaultElasticSearchKeys[key],
                          })
                        )}
                        optionKey="value"
                        onChange={(value) => {
                          onKeyChange({
                            key: 'catalog_store_key',
                            id: searchKey.id,
                            catalog_store_key:
                              value.map((obj) => obj.key) || [],
                          });
                        }}
                        value={
                          searchKey.value.keys.map((key) => ({
                            key,
                            value: defaultElasticSearchKeys[key],
                          })) || []
                        }
                        disabled={disableAllFields}
                      />
                      <Box padding={0} marginTop="6px">
                        <ExactMatchCheckbox
                          themed
                          label="Exact Match"
                          style={{ marginTop: '6px' }}
                          onChange={(value) => {
                            onKeyChange({
                              key: 'exact_match',
                              id: searchKey.id,
                              exact_match: value,
                            });
                          }}
                          value={searchKey.value.exact_match}
                          disabled={disableAllFields}
                        />
                      </Box>

                      <BinIcon
                        iconColor="red.haptik"
                        size="small"
                        marginLeft="-16px"
                        marginTop="2px"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (disableAllFields) return;
                          onKeyChange({
                            key: 'remove',
                            value: {
                              id: searchKey.id,
                            },
                          });
                        }}
                      />
                    </Box>
                  ))}
                  {errors.searchKey && (
                    <Text color="red.haptik" variant="h4.semi-bold">
                      {' '}
                      {errors.searchKey}
                    </Text>
                  )}
                </Box>
              </>
            ) : (
              <Box
                padding={0}
                alignItems="center"
                height="30px"
                marginBottom="10px"
                style={{ fontSize: '14px', fontWeight: '400' }}
              >
                No search keys are present
              </Box>
            )}
            <Box
              padding={0}
              alignItems="center"
              marginTop="6px"
              onClick={() => {
                if (disableAllFields) return;
                onKeyChange({
                  key: 'new_key',
                  value: {
                    id: (gptToElasticSearchKeys.length ?? 0) + 1,
                    key: '',
                    value: { keys: [], exact_match: false },
                  },
                });
              }}
              style={{
                cursor: disableAllFields ? 'not-allowed' : 'pointer',
              }}
            >
              <AddIconV2
                iconColor="blue.haptik"
                marginRight="8px"
                size="small"
              />
              <Text color="blue.haptik" variant="body.sm">
                Add search key
              </Text>
            </Box>
          </Box>
        </DefaultCursorWrapper>
      )}
    </CustomPromptsSearchSettingsWrapper>
  );
};

export default CustomPromptsSearchSettings;
